<script setup>
import { computed, onBeforeMount, unref } from "vue";
import { useVModel } from "@vueuse/core";
import { useVuelidate } from "@vuelidate/core";

import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import RadioYesNo from "@/components/shared/input/RadioYesNo.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import { updateFieldToDecimals } from "@/services/utils";

import { checkIfProgramSessionTypeIsInternshipOnly } from "@/components/program-manager/sessions/utils.js";
import { sessionType } from "@/components/program-manager/sessions/constants.js";
import {
  acceptanceDecisions,
  gpaExceptionRequirementsOptions,
  minYearInSchoolOptions,
  proficiencyLevelsOptions,
  requiredLanguagesOptions,
  learnerTypesOptions,
} from "@/components/program-manager/sessions/constants";
import { toTwoDecimals } from "@/services/utils";

const props = defineProps({
  modelValue: {
    type: Object,
  },
  rules: {
    type: Object,
  },
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const isSessionTypeInternship = computed(
  () => !!programSession.value?.session_types?.find((val) => val.id == sessionType.internship)
);

const hasProgramSessionTypeInternshipOnly = computed(() =>
  checkIfProgramSessionTypeIsInternshipOnly(unref(programSession))
);

let v$ = {};
onBeforeMount(() => {
  v$ = useVuelidate(props.rules, programSession, {
    $registerAs: "ParticipantsSection",
    $lazy: true,
  });
});
</script>
<template>
  <div class="grid grid-cols-2 gap-x-24 gap-y-4">
    <CheckboxSelect
      v-model="programSession.education_levels"
      :vuelidate-instance="v$.education_levels"
      :options="learnerTypesOptions"
      field-label="Open to"
      :multiple="true"
      :deselect-from-dropdown="true"
      :close-on-select="false"
      data-testid="program-session-education-levels-checkbox"
      placeholder="Pick a value"
      label="value"
    />
    <CheckboxSelect
      v-model="programSession.acceptance_decisions"
      :vuelidate-instance="v$.acceptance_decisions"
      :options="acceptanceDecisions"
      :multiple="true"
      :deselect-from-dropdown="true"
      :close-on-select="false"
      data-testid="program-session-acceptance-decisions-checkbox"
      placeholder="Pick a value"
      label="name"
      field-label="Admission Decision"
    />
    <RadioYesNo
      v-model="programSession.gpa_exceptions_allowed"
      input-classes="h-10 w-full form-input"
      :vuelidate-instance="v$.gpa_exceptions_allowed"
      type="number"
      field-label="GPA Exceptions Allowed?"
      data-testid="program-session-gpa-exceptions-allowed-radio"
    />
    <CheckboxSelect
      v-model="programSession.gpa_exception_requirements"
      :options="gpaExceptionRequirementsOptions"
      label="name"
      field-label="GPA Exception Requirements"
      :vuelidate-instance="v$.gpa_exception_requirements"
      :multiple="true"
      :deselect-from-dropdown="true"
      :close-on-select="false"
      data-testid="program-session-education-requirements-checkbox"
      placeholder="Pick a value"
    />
    <BasicTextInput
      v-model="programSession.minimum_gpa_requirement"
      input-classes="h-10 w-full form-input"
      :vuelidate-instance="v$.minimum_gpa_requirement"
      type="number"
      min="0"
      step=".01"
      data-testid="program-session-minimum-gpa-requirement-text-input"
      @blur="() => updateFieldToDecimals('minimum_gpa_requirement', programSession)"
    >
      <template #fieldLabel>
        <label class="common-label-text"
          ><span v-if="!hasProgramSessionTypeInternshipOnly" class="text-red-100 pr-2">**</span>Minimum GPA
          Required</label
        >
      </template>
    </BasicTextInput>
    <v-select-caret
      v-model="programSession.academic_level_id"
      :vuelidate-instance="v$.academic_level_id"
      :options="minYearInSchoolOptions"
      :reduce="(val) => val.id"
      data-testid="program-session-academic-level-id-select"
      label="academic_year"
      field-label="Minimum Year in School / Age"
    />
    <div class="flex items-center">
      <label
        for="education_level_minimum_age_rule"
        class="inline-flex items-center rounded-full p-1 cursor-pointer text-gray-500 bg-gray-300 h-10 mr-2"
      >
        <input
          id="education_level_minimum_age_rule"
          :modelValue="programSession.education_level_minimum_age_rule !== 'or'"
          type="checkbox"
          class="hidden peer"
          data-testid="program-session-education-level-minimum-age-rule-text-input"
          :disabled="!programSession.minimum_age && !programSession.academic_level_id"
          @update:modelValue="
            (val) =>
              val
                ? (programSession.education_level_minimum_age_rule = 'and')
                : (programSession.education_level_minimum_age_rule = 'or')
          "
        />
        <span class="px-4 py-1 rounded-full bg-blue-700 text-white peer-checked:bg-gray-300 peer-checked:text-black"
          >AND</span
        >
        <span class="px-4 py-1 rounded-full bg-gray-300 text-black peer-checked:bg-blue-700 peer-checked:text-white"
          >OR</span
        >
      </label>
      <BasicTextInput
        v-model="programSession.minimum_age"
        input-classes="h-10 w-full form-input"
        :vuelidate-instance="v$.minimum_age"
        data-testid="program-session-minimum-age-text-input"
        type="number"
        min="0"
        outer-classes="flex items-center"
      >
        <template #fieldLabel>
          <span class="common-label-text mr-2">Age</span>
        </template>
      </BasicTextInput>
    </div>
    <CheckboxSelect
      v-model="programSession.required_languages"
      :vuelidate-instance="v$.required_languages"
      :options="requiredLanguagesOptions"
      :multiple="true"
      :deselect-from-dropdown="true"
      :close-on-select="false"
      data-testid="program-session-required-languages-checkbox"
      placeholder="Pick a value"
      label="value"
      field-label="Language(s) Required"
    />
    <v-select-caret
      v-model="programSession.min_proficiency_level_id"
      :vuelidate-instance="v$.min_proficiency_level_id"
      :options="proficiencyLevelsOptions"
      :reduce="(val) => val.id"
      data-testid="program-session-min-proficiency-level-id-select"
      field-label="Language Level Required"
      label="value"
    />
    <BasicTextInput
      v-model="programSession.difficulty_score"
      input-classes="h-10 w-full form-input"
      :vuelidate-instance="v$.difficulty_score"
      data-testid="program-session-difficulty-score-text-input"
      type="number"
      min="0"
      step=".01"
      field-label="Acceptance Difficulty"
      @blur="() => (programSession.difficulty_score = toTwoDecimals(programSession.difficulty_score))"
    />
    <BasicTextInput
      v-model="programSession.prerequisites"
      data-testid="program-session-prerequisites-text-input"
      outer-classes="col-span-2"
      input-classes="h-10 w-full form-input"
      :vuelidate-instance="v$.prerequisites"
      field-label="Pre-requisites"
    />
    <BasicTextInput
      v-model="programSession.corequisities"
      data-testid="program-session-corequisities-text-input"
      outer-classes="col-span-2"
      input-classes="h-10 w-full form-input"
      :vuelidate-instance="v$.corequisities"
      field-label="Co-requisites"
    />
    <BasicTextInput
      v-show="isSessionTypeInternship"
      v-model="programSession.maximum_age"
      data-testid="program-session-maximum-age-text-input"
      input-classes="h-10 w-full form-input"
      :vuelidate-instance="v$.maximum_age"
      type="number"
      min="0"
      field-label="Max Age"
    />
  </div>
</template>
